import type { AsyncData, NuxtError } from '#app';
import type { User } from '@microsoft/microsoft-graph-types';
import type { UserProfileImage } from '~/constants/types/UserProfileImage';
import type { ComputedRef } from 'vue';
import { computed, watch } from 'vue';
import { useUserStore } from '~/stores/userStore';

type UseUserReturn = {
  userState: AsyncData<User | null, NuxtError<unknown> | NuxtError<null> | undefined>;
  userImageState: AsyncData<UserProfileImage | null, NuxtError<unknown> | NuxtError<null> | undefined>;
  userInitials: ComputedRef<string | null>;
  store: ReturnType<typeof useUserStore>;
};

export function useUser(): UseUserReturn {

  /** Store */
  const store = useUserStore();

  /** Plugins */
  const { $profileInfo, $profileImg } = useNuxtApp();

  /** Async States */
  const userState = useAsyncData<User | null, NuxtError<unknown> | null>('user', () => $profileInfo(), {
    server: false,
    immediate: false,
    transform: (res): User | null => res ?? null,
    default: () => null,
  });

  const userImageState = useAsyncData<UserProfileImage | null, NuxtError<unknown> | null>('userImage', () => $profileImg(), {
    server: false,
    immediate: false,
    transform: (res): UserProfileImage | null => res ?? null,
    default: () => null,
  });

  /** Getters */
  const userInitials = computed<string | null>(() => {
    // Return initials if there's no profile image (error) and we have user data
    if (userImageState.error.value && userState.data.value) {
      return getInitials();
    }
    return null;
  });

  /** Actions */
  function getInitials(): string | null {
    if (!userState.data.value?.displayName) {
      return null;
    }

    const nameArray = userState.data.value.displayName.split(' ');
    const firstNameInitial = nameArray[0]?.charAt(0) ?? '';
    const surnameInitial = nameArray[1]?.charAt(0) ?? '';

    return firstNameInitial + surnameInitial || null;
  }

  watch(
    () => userState.data.value,
    (newValue) => store.setUser(newValue ?? null),
    { deep: true }
  );

  watch(() => userImageState.data.value, () => store.setUserImage(userImageState.data.value ?? null), { deep: true });

  watch(() => userInitials.value, () => store.setUserInitials(userInitials.value), { deep: true });

  return {
    userState,
    userImageState,
    userInitials,
    store,
  }
}
